import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Continue from '@components/atoms/Button/Continue';
import RadioIconField from '@components/molecules/RadioIconField/RadioIconField';
import RadioField from '@components/molecules/RadioField/RadioField';
import CheckBoxField from '@components/molecules/CheckBoxField/CheckBoxField';
import SelectField from '@components/molecules/SelectField/SelectField';
import MultiStepTitle from '@components/molecules/MultiStepTitle/MultiStepTitle';
import { Field, FlexField } from './StepElement.styled';
import TextField from '@components/molecules/TextField/TextField';
import { useMultiStepContext } from '@components/hoc/MultiStepForm/MultiStepFormContext';
import('@components/atoms/Options/SituationProOptions');
import {
  situationProOptions,
  genderOptions,
  howDiscovered,
  objectifOptions,
  financementOptions,
  formationContextOptions,
  etudesOptions,
  domains,
  formationsAlternance,
  excludeFromCpfFinancement,
  CPF_FINANCEMENT_CODE
} from '@config/lists';
import { frenchDepartments } from '@config/frDepartements';
import { handleStep } from '@actions/formManipulation';
import GridIconField from '@components/molecules/GridIconField/GridIconField';
import DetailedRadioIconField from '@components/molecules/DetailedRadioIconField/DetailedRadioIconField';
import { isEmail, isValidTelNumber, isEnDateOfBirth } from '@actions/formValidation';
import { isMobile } from 'react-device-detect';

const StepElement = ({
  stepData,
  formDataContext,
  trainingId,
  handleNextStep,
  phrases,
  handleFormData,
  currentStep,
  maxStep,
  introPage,
}) => {
  const [stepFields, setStepFields] = useState([]);
  const [disabled, setDisabled] = useState(true);
  const {visibleSteps, setConfig} = useMultiStepContext();

  useEffect(() => {
    setStepFields([...stepData?.fields] || null);
  }, [stepData]);

  const changeListener = (event) => {
    if (event.target.nodeName === 'DIV') {
      event.target = event.target.firstChild;
    }
    let fieldsData = {};
    fieldsData['name'] = event.target.name || event.target.id;
    fieldsData['value'] = event.target.value;
    if (event.target.type === 'checkbox') {
      fieldsData['value'] = event.target.checked ? 'on' : 'off';
    }
    handleFormData(fieldsData);
  };

  const lists = [
    { name: 'situationPro', component: situationProOptions },
    { name: 'gender', component: genderOptions },
    { name: 'howDiscovered', component: howDiscovered },
    { name: 'objectif', component: objectifOptions },
    { name: 'financement', component: financementOptions },
    { name: 'formationContext', component: formationContextOptions },
    { name: 'niveauEtudes', component: etudesOptions },
    { name: 'domains', component: domains },
    { name: 'departements', component: frenchDepartments}
  ];

  const existingConditions = {
    alternance: {
      condition: (bitrixId) => {
        return (
          formationsAlternance.filter((formation) => {
            return formation.bitrixId === Number(bitrixId);
          }).length > 0
        );
      },
      callback: (item) => !item.name.includes('alternance'),
    },
  };


  const fillVisibleSteps = (stepId, value) => {

    handleStep(stepId, value, visibleSteps, setConfig);
  };


  const handleContinue = (stepId, value) => {
    if (currentStep !== maxStep) {
      fillVisibleSteps(stepId, value);
      handleNextStep();
    }
  };

  const retrieveOptions = (option) => {
    const optionObj = lists.filter((item) => item.name === option);

    return optionObj[0].component;
  };

  // Fonction de vérification des étapes avec des cas particuliers pour les champs de type email, flex et tel.
  const checkStep = (field) => {
    switch (field.type) {
    case 'email':
      return isEmail(formDataContext[field.name]);
    case 'tel':
      return isValidTelNumber(formDataContext[field.name]);
    case 'flexInput':
      // Return validity of each fields inside a composite field
      let validityOfInsideComponents = field.components.map((fieldInFlex) => {
        return !fieldInFlex.required || !!formDataContext[fieldInFlex.name];
      });
        //Check that every value inside a composed field is valid
      return validityOfInsideComponents.every((item) => item);
    case 'consentCheckbox':
      return (
        !field.required ||
          (field.required && formDataContext[field.name] === 'on')
      );
    case 'date':
      return isEnDateOfBirth(formDataContext[field.name]);
    default:
      return !field.required || !!formDataContext[field.name];
    }
  };

  // Le useEffect de vérification de chaque étape dans son intégralité et va vérifier de manière agnostique chaque étape
  useEffect(() => {
    if (stepFields && stepFields.map(checkStep).every((item) => item)) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [stepFields, formDataContext]);

  // Rajout d'un scrollToTop dès que l'élément StepElement est chargé (donc à chaque changement d'étapes)
  // et uniquement lors du premier affichage (array dep vide)
  useEffect(() => {
    if (isMobile) {
      window.scrollTo({ top: 0, left: 0 });
    }
  }, []);


  const retrieveOptionsConditioned = (option, conditionObj) => {

    const optionObj = lists.filter((item) => item.name === option);
    let completeList = optionObj[0].component;
    if (option === 'financement' && excludeFromCpfFinancement.includes(trainingId)) {
      completeList = completeList.filter(elem => elem.id !== CPF_FINANCEMENT_CODE);
    }

    if (!existingConditions[conditionObj]?.condition(trainingId)) {

      return completeList.filter(existingConditions[conditionObj].callback);
    }

    return completeList;

  };

  //build a select
  const displayField = (fieldData) => {

    switch (fieldData.type) {
    case 'radio':
      return (
        <RadioField
          key={fieldData?.name}
          id={fieldData?.name}
          label={fieldData?.label || ''}
          required={fieldData?.required}
          name={fieldData?.name}
          opts={retrieveOptions(fieldData?.options)}
          className="form-control"
          value={formDataContext[fieldData?.name] || ''}
          onClick={changeListener.bind(this)}
        />
      );
    case 'radioIcon':
      return (
        <RadioIconField
          contextValue={formDataContext[fieldData?.name]}
          handleFormData={handleFormData}
          handleEvent={handleContinue}
          options={retrieveOptions(fieldData?.options)}
          key={fieldData?.name}
          id={fieldData?.name}
        />
      );
    case 'detailedRadioIcon':
      return (
        <DetailedRadioIconField
          contextValue={formDataContext[fieldData?.name]}
          handleFormData={handleFormData}
          handleEvent={handleContinue}
          dataSet={fieldData?.dataSet}
          domainId={formDataContext.domainId}
          key={fieldData?.name}
          id={fieldData?.name}
        />
      );
    case 'radioIconConditioned':
      return (
        <RadioIconField
          contextValue={formDataContext[fieldData?.name]}
          handleFormData={handleFormData}
          handleEvent={handleContinue}
          options={retrieveOptionsConditioned(
            fieldData?.options,
            fieldData?.condition
          )}
          key={fieldData?.name}
          id={fieldData?.name}
        />
      );
    case 'domainsIcon':
      return (
        <GridIconField
          contextValue={formDataContext['domainId']}
          handleFormData={handleFormData}
          handleEvent={handleContinue}
          options={retrieveOptions(fieldData?.options)}
          key={fieldData?.name}
          id={'domainId'}
        />
      );
    case 'consentCheckbox':
      let consentPhrase =
          phrases && phrases.filter((phrase) => phrase.key === fieldData?.name);
      return (
        consentPhrase && (
          <CheckBoxField
            key={consentPhrase[0]?.key}
            label={consentPhrase[0] ? consentPhrase[0]?.phrase : ''}
            required={fieldData?.required}
            id={consentPhrase[0]?.key}
            name={consentPhrase[0]?.key}
            isConsentBox={true}
            className="form-control"
            isChecked={
              formDataContext[fieldData?.name] === 'on'
            }
            onClick={changeListener.bind(this)}
          />
        )
      );

    case 'flexInput':
      return (
        <FlexField key={fieldData?.name}>
          {fieldData?.components.map((flexComponent) => (
            <Field key={flexComponent?.name}>
              <TextField
                key={flexComponent?.name}
                label={flexComponent?.label}
                sublabel={flexComponent?.sublabel}
                type={flexComponent?.type}
                required={flexComponent?.required}
                id={flexComponent?.name}
                pattern={flexComponent?.pattern}
                name={flexComponent?.name}
                placeholder={flexComponent?.placeholder}
                className="form-control"
                value={formDataContext[flexComponent?.name] || ''}
                onChange={changeListener.bind(this)}
              />
            </Field>
          ))}
        </FlexField>
      );
    case 'select':
      return (
        <FlexField key={fieldData?.name}>

          <Field key={fieldData?.name}>
            <SelectField
              placeholder={fieldData?.placeholder}
              name={fieldData?.name}
              required={fieldData?.required}
              className="form-control"
              value={formDataContext[fieldData?.name]}
              handleFormData={handleFormData}
              handleEvent={handleContinue}
              options={retrieveOptions(fieldData?.options)}
              key={fieldData?.name}
              id={fieldData?.name}
              labelCallback={fieldData?.labelCallback}

            />
          </Field>

        </FlexField>
      );

    default:
      return (
        <Field key={fieldData?.name}>
          <TextField
            key={fieldData?.name}
            label={fieldData?.label}
            sublabel={fieldData?.sublabel}
            type={fieldData?.type}
            required={fieldData?.required}
            id={fieldData?.name}
            pattern={fieldData?.pattern}
            name={fieldData?.name}
            placeholder={fieldData?.placeholder}
            min={fieldData?.min}
            max={fieldData?.max}
            className="form-control"
            value={formDataContext[fieldData?.name] || ''}
            onChange={changeListener.bind(this)}
          />
        </Field>
      );
    }
  };

  return (
    <>
      <MultiStepTitle
        title={stepData.title}
        subtitle={stepData?.subtext}
      />
      {stepFields.map((field) => displayField(field))}
      {!introPage && (
        <Continue
          id="continue"
          label={
            currentStep === maxStep ? 'Obtenir des informations' : 'Continuer'
          }
          disabled={!!disabled}
          type={currentStep === maxStep ? 'submit' : 'button'}
          onClickHandler={handleContinue}
        >
        </Continue>
      )}
    </>
  );
};

StepElement.propTypes = {
  stepData: PropTypes.object.isRequired,
  formDataContext: PropTypes.object.isRequired,
  handleNextStep: PropTypes.func.isRequired,
  handleFormData: PropTypes.func.isRequired,
};

export default StepElement;
